import { useEffect } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// App theme. 'system' follows the OS via prefers-color-scheme and tracks live
// changes. The resolved 'light' | 'dark' is written to <html data-theme>, which
// drives the CSS-variable palettes in index.css. A tiny inline script in
// index.html applies the same value before first paint to avoid a flash.
export type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeState {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'system',
      setMode: (mode) => set({ mode }),
    }),
    { name: 'msp-secops-theme' }
  )
)

const prefersDark = () => window.matchMedia('(prefers-color-scheme: dark)').matches

export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  return mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode
}

function applyTheme(mode: ThemeMode): void {
  document.documentElement.setAttribute('data-theme', resolveTheme(mode))
}

/** Apply the current theme and, in system mode, follow OS changes live. */
export function useApplyTheme(): void {
  const mode = useThemeStore((s) => s.mode)
  useEffect(() => {
    applyTheme(mode)
    if (mode !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => applyTheme('system')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [mode])
}
